import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, CreditCard, FileText, Truck, DollarSign, Mail,
  MessageSquare, CalendarClock, ClipboardList, Trash2, Plus, ExternalLink, CheckCircle2, Users,
} from "lucide-react";
import { ProofControl, ProductionStageSelect } from "@/pages/Admin";

const fmt$ = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const dt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return String(iso); }
};

/**
 * Supplier Project — one unified page per Prime Supplier.
 * Aggregates Registration, Production, Logistics, Finance, Documents,
 * Communications, Business Matchmaking, Internal Notes, and Activity Timeline.
 * Backed by GET /api/admin/supplier-projects/{id}.
 */
export default function SupplierProject() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/supplier-projects/${id}`);
      setData(data);
    } catch (e) {
      toast.error("Could not load supplier project.");
    } finally { setLoadingData(false); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { nav("/login"); return; }
    load();
  }, [user, loading, id]);

  if (loading || loadingData) return <Layout><section className="container-summit py-24"><div className="overline">Loading supplier project…</div></section></Layout>;
  if (!data) return <Layout><section className="container-summit py-24 text-center"><h1 className="text-2xl">Supplier not found.</h1><Link to="/admin/registration" className="btn-outline-black mt-6 inline-flex">Back to Registration</Link></section></Layout>;

  const { exhibitor, station, production, proof, appointments, logistics, finance, communications, notes, documents, activity } = data;

  return (
    <Layout>
      {/* Header */}
      <section className="bg-[#0A0A0A] text-white border-b border-white/10">
        <div className="container-summit py-8 md:py-10">
          <Link to="/admin/registration" className="text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-white inline-flex items-center gap-2" data-testid="sp-back">
            <ArrowLeft size={12} /> All Suppliers
          </Link>
          <div className="mt-5 flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="overline text-white/60"><span className="accent-rule" />Supplier Project</div>
              <h1 className="serif text-4xl sm:text-5xl mt-3" data-testid="sp-company">{exhibitor.company_name}</h1>
              <p className="mt-3 text-sm text-white/70">
                {exhibitor.primary_contact ? `${exhibitor.primary_contact} · ` : ""}{exhibitor.email}{exhibitor.phone ? ` · ${exhibitor.phone}` : ""}
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10" data-testid="sp-header-kpis">
              <HKpi label="Payment" value={(exhibitor.payment_status || "—").toUpperCase()} tone={exhibitor.payment_status === "paid" ? "text-emerald-400" : "text-white"} />
              <HKpi label="Station" value={station?.label || "—"} tone={station ? "text-[#A39171]" : "text-white/60"} />
              <HKpi label="Stage" value={(production.stage || "—").replace(/_/g, " ")} tone="text-white" />
              <HKpi label="Proof" value={proof.status ? proof.status.replace(/_/g, " ") : "—"} tone={proof.status === "approved" ? "text-emerald-400" : proof.status === "revision_requested" ? "text-amber-400" : proof.status === "ready" ? "text-[#A39171]" : "text-white/60"} />
            </div>
          </div>
        </div>
      </section>

      <section className="container-summit py-10 grid gap-8">
        {/* Overview */}
        <Section testId="sp-overview" title="Project Overview" icon={ClipboardList}>
          <div className="grid md:grid-cols-3 gap-6">
            <OverviewStat label="Supplier Revenue" value={fmt$(finance.supplier_revenue)} icon={DollarSign} tone="text-emerald-700" />
            <OverviewStat label="Total Expenses" value={fmt$(finance.total_expenses)} icon={DollarSign} tone="text-red-700" />
            <OverviewStat label="Net Margin" value={fmt$(finance.gross_margin)} icon={DollarSign} tone={finance.gross_margin >= 0 ? "text-emerald-700" : "text-red-700"} />
          </div>
          <div className="mt-6 grid md:grid-cols-3 gap-6 text-sm">
            <Meta label="Registered" value={dt(exhibitor.created_at)} />
            <Meta label="Logo Received" value={exhibitor.logo_file_id ? "Yes" : "Pending"} />
            <Meta label="Booth / Station" value={station?.label || "Not assigned"} />
          </div>
        </Section>

        {/* Registration */}
        <Section testId="sp-registration" title="Registration" icon={CreditCard}>
          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <Meta label="Company" value={exhibitor.company_name} />
            <Meta label="Primary Contact" value={exhibitor.primary_contact || "—"} />
            <Meta label="Contact Title" value={exhibitor.contact_title || "—"} />
            <Meta label="Email" value={exhibitor.email} />
            <Meta label="Phone" value={exhibitor.phone || "—"} />
            <WebsiteField exhibitor={exhibitor} />
            <IndustryCategoryField exhibitor={exhibitor} onSaved={() => load()} />
            <Meta label="Assigned Station" value={exhibitor.booth_number || "—"} />
            <Meta label="Payment Status" value={formatPaymentStatus(exhibitor.payment_status)} />
            <Meta label="Registration Status" value={exhibitor.status || "—"} />
          </div>

          {/* Billing address — required for Display America invoicing */}
          <div className="mt-6 border-t border-zinc-200 pt-6">
            <div className="overline mb-3">Billing Address · For Display America Invoice</div>
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <Meta label="Address Line 1" value={exhibitor.billing_address_line1 || "—"} />
              <Meta label="Address Line 2" value={exhibitor.billing_address_line2 || "—"} />
              <Meta label="City" value={exhibitor.billing_city || "—"} />
              <Meta label="State / Province" value={exhibitor.billing_state || "—"} />
              <Meta label="ZIP / Postal Code" value={exhibitor.billing_postal_code || "—"} />
              <Meta label="Country" value={exhibitor.billing_country || "—"} />
            </div>
          </div>

          <SupplierProfileEditor exhibitor={exhibitor} onSaved={() => load()} />
        </Section>

        {/* Company Representatives — Primary + optional Additional (Aug 2026) */}
        <Section testId="sp-representatives" title="Company Representatives" icon={CreditCard}>
          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <div className="border-l-2 border-[#A39171] pl-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#A39171]">Primary Representative</div>
              <div className="font-medium mt-1">{exhibitor.primary_contact || "—"}</div>
              <div className="text-zinc-500 text-xs">{exhibitor.contact_title || "—"}</div>
              <div className="text-zinc-500 text-xs">{exhibitor.email}</div>
              {exhibitor.phone && <div className="text-zinc-500 text-xs">{exhibitor.phone}</div>}
            </div>
            {exhibitor.additional_representative ? (
              <div className="border-l-2 border-zinc-300 pl-4" data-testid="sp-additional-rep">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Additional Representative</div>
                <div className="font-medium mt-1">
                  {exhibitor.additional_representative.first_name} {exhibitor.additional_representative.last_name}
                </div>
                <div className="text-zinc-500 text-xs">{exhibitor.additional_representative.title || "—"}</div>
                <div className="text-zinc-500 text-xs">{exhibitor.additional_representative.email}</div>
                {exhibitor.additional_representative.phone && (
                  <div className="text-zinc-500 text-xs">{exhibitor.additional_representative.phone}</div>
                )}
              </div>
            ) : (
              <div className="border-l-2 border-zinc-200 pl-4 text-zinc-500 text-xs italic">
                No Additional Representative registered yet.
              </div>
            )}
          </div>
        </Section>

        {/* Invoicing — QuickBooks manual reconciliation (Feb 2026 · Phase 1) */}
        <Section testId="sp-invoicing" title="Invoicing" icon={CreditCard}>
          <InvoicingPanel exhibitor={exhibitor} onSaved={() => load()} />
        </Section>

        {/* Production */}
        <Section testId="sp-production" title="Production" icon={FileText}>
          <div className="mb-4 max-w-xs">
            <ProductionStageSelect exhibitor={exhibitor} onSaved={() => load()} />
          </div>
          <ol className="grid gap-1">
            {production.stages.map((s, i) => (
              <li key={s.key} className="flex items-baseline gap-3 py-1.5" data-testid={`sp-stage-${s.key}`}>
                <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${s.current ? "bg-[#A39171] ring-4 ring-[#A39171]/15" : s.complete ? "bg-black" : "bg-zinc-200"}`} />
                <span className={`text-sm ${s.current ? "font-medium text-black" : s.complete ? "text-zinc-700" : "text-zinc-400"}`}>{s.label}</span>
                {s.current && <span className="text-[10px] uppercase tracking-[0.22em] text-[#A39171] ml-2">In progress</span>}
              </li>
            ))}
          </ol>
          <div className="mt-6 border-t border-zinc-100 pt-5">
            <div className="overline mb-3">Proof</div>
            <ProofControl exhibitor={exhibitor} onSaved={() => load()} />
            {proof.revision_comment && (
              <div className="mt-3 border-l-2 border-amber-500 pl-3 text-xs text-zinc-700 max-w-lg">
                <div className="overline mb-1">Supplier comment</div>
                {proof.revision_comment}
              </div>
            )}
          </div>
        </Section>

        {/* Logistics */}
        <Section testId="sp-logistics" title="Logistics" icon={Truck}
                actionHref="/admin/logistics" actionLabel="Manage in Logistics workspace">
          {logistics.length === 0 ? (
            <div className="text-sm text-zinc-500">No supplier-specific logistics tasks yet.</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {logistics.map((t) => (
                <li key={t.id} className="py-3 flex items-baseline justify-between gap-3" data-testid={`sp-logistics-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {t.category} {t.owner ? `· ${t.owner}` : ""} {t.due_date ? `· due ${t.due_date}` : ""}
                    </div>
                  </div>
                  <span className="pill">{t.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Finance */}
        <Section testId="sp-finance" title="Finance" icon={DollarSign}
                actionHref="/admin/finance" actionLabel="Manage in Finance workspace">
          <div className="grid md:grid-cols-3 gap-4 mb-5">
            <MiniKpi label="Revenue" value={fmt$(finance.supplier_revenue)} tone="text-emerald-700" />
            <MiniKpi label="Expenses" value={fmt$(finance.total_expenses)} tone="text-red-700" />
            <MiniKpi label="Margin" value={fmt$(finance.gross_margin)} tone={finance.gross_margin >= 0 ? "text-emerald-700" : "text-red-700"} />
          </div>
          {finance.expenses.length === 0 ? (
            <div className="text-sm text-zinc-500">No supplier-specific expenses recorded.</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {finance.expenses.map((e) => (
                <li key={e.id} className="py-3 flex items-baseline justify-between gap-3" data-testid={`sp-expense-${e.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{e.description}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {e.category} {e.vendor ? `· ${e.vendor}` : ""} {e.date ? `· ${e.date}` : ""}
                    </div>
                  </div>
                  <span className="text-sm tabular-nums font-medium">{fmt$(e.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Documents */}
        <Section testId="sp-documents" title="Documents" icon={FileText}>
          {documents.length === 0 ? (
            <div className="text-sm text-zinc-500">No supplier documents yet.</div>
          ) : (
            <ul className="grid gap-2">
              {documents.map((d) => (
                <li key={d.kind} className="flex items-center gap-3 border-b border-zinc-100 py-2 last:border-0" data-testid={`sp-doc-${d.kind}`}>
                  <FileText size={14} className="text-[#A39171] shrink-0" strokeWidth={1.4} />
                  <a href={`${BACKEND_URL}/api/files/${d.file_id}`} target="_blank" rel="noreferrer" className="text-sm flex-1 underline underline-offset-4 truncate">
                    {d.label}
                  </a>
                  <ExternalLink size={12} className="text-zinc-400" />
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Communications */}
        <Section testId="sp-communications" title="Communications" icon={Mail}>
          {communications.length === 0 ? (
            <div className="text-sm text-zinc-500">No emails on record yet.</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {communications.slice(0, 20).map((m, i) => (
                <li key={i} className="py-3 flex items-baseline justify-between gap-3" data-testid={`sp-email-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{m.subject || "(no subject)"}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {m.kind || "—"} {m.status_code ? `· ${m.status_code}` : ""} · {dt(m.created_at)}
                    </div>
                  </div>
                  {m.ok ? <CheckCircle2 size={12} className="text-emerald-600" /> : <span className="text-[10px] uppercase tracking-[0.18em] text-red-600">Failed</span>}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Matchmaking */}
        <Section testId="sp-matchmaking" title="Business Matchmaking" icon={Users}>
          {appointments.length === 0 ? (
            <div className="text-sm text-zinc-500">No meetings scheduled yet.</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {appointments.map((a) => (
                <li key={a.id} className="py-3 flex items-baseline justify-between gap-3" data-testid={`sp-appt-${a.id}`}>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 tabular-nums">{dt(a.start_iso || a.created_at)}</div>
                    <div className="text-sm font-medium mt-1">
                      {a.counterpart || a.tl_leader_name || "—"}
                      {a.kind === "tl_meeting" && <span className="ml-2 text-[9px] uppercase tracking-[0.16em] px-1 py-[1px] border border-indigo-300 text-indigo-700">T+L Sourcing</span>}
                    </div>
                    {a.location && <div className="text-xs text-zinc-500 mt-0.5">{a.location}</div>}
                  </div>
                  <span className="pill">{a.status || "scheduled"}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Internal Notes */}
        <NotesSection notes={notes} exhibitorId={id} onChanged={load} />

        {/* Activity Timeline */}
        <Section testId="sp-activity" title="Activity Timeline" icon={CalendarClock}>
          {activity.length === 0 ? (
            <div className="text-sm text-zinc-500">No activity yet.</div>
          ) : (
            <ul className="grid gap-3">
              {activity.slice(0, 40).map((a, i) => (
                <li key={i} className="flex items-baseline gap-3" data-testid={`sp-activity-${i}`}>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 tabular-nums w-28 shrink-0">{dt(a.ts)}</span>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#A39171] w-24 shrink-0">{a.kind}</span>
                  <span className="text-sm">{a.label}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </section>
    </Layout>
  );
}

const PAYMENT_STATUS_LABELS = {
  awaiting_invoice:  "Awaiting Invoice",
  invoice_sent:      "Invoice Sent",
  payment_received:  "Payment Received",
  complete:          "Registration Complete",
  cancelled:         "Cancelled",
  paid:              "Payment Received",   // legacy
  unpaid:            "Awaiting Invoice",   // legacy
  pending:           "Awaiting Invoice",   // legacy
};

function formatPaymentStatus(s) {
  if (!s) return "—";
  return PAYMENT_STATUS_LABELS[s] || s.replace(/_/g, " ");
}

/**
 * Invoicing panel — Feb 2026 · QuickBooks manual reconciliation.
 * Admin captures the QuickBooks invoice number, dates, and amounts;
 * emails the invoice link; and marks paid once the supplier pays inside
 * QuickBooks. Cancelling releases the station.
 */
function InvoicingPanel({ exhibitor, onSaved }) {
  const [f, setF] = useState({
    invoice_number: exhibitor.invoice_number || "",
    invoice_date:   exhibitor.invoice_date   || "",
    invoice_amount: exhibitor.invoice_amount ?? 2500,
    invoice_url:    exhibitor.invoice_url    || "",
    invoice_status: exhibitor.invoice_status || "not_sent",
    payment_due_date: exhibitor.payment_due_date || "",
    payment_received_date: exhibitor.payment_received_date || "",
    amount_paid:    exhibitor.amount_paid ?? "",
    accounting_notes: exhibitor.accounting_notes || "",
  });
  const [busy, setBusy] = useState(null);
  const status = exhibitor.payment_status || "";
  const isPaid = ["payment_received", "complete", "paid"].includes(status);
  const isCancelled = status === "cancelled";

  const save = async () => {
    setBusy("save");
    try {
      await api.put(`/admin/exhibitors/${exhibitor.id}/invoice`, {
        invoice_number: f.invoice_number || null,
        invoice_date:   f.invoice_date   || null,
        invoice_amount: f.invoice_amount === "" ? null : Number(f.invoice_amount),
        invoice_url:    f.invoice_url    || null,
        invoice_status: f.invoice_status || null,
        payment_due_date: f.payment_due_date || null,
        payment_received_date: f.payment_received_date || null,
        amount_paid: f.amount_paid === "" ? null : Number(f.amount_paid),
        accounting_notes: f.accounting_notes || null,
      });
      toast.success("Invoice details saved.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save invoice.");
    } finally { setBusy(null); }
  };

  const setPaymentStatus = async (val) => {
    setBusy("status");
    try {
      await api.put(`/admin/exhibitors/${exhibitor.id}/payment`, { payment_status: val });
      toast.success("Payment status updated.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update payment status.");
    } finally { setBusy(null); }
  };

  const sendEmail = async () => {
    if (!f.invoice_url) return toast.error("Add an invoice URL first.");
    if (!window.confirm(`Email the invoice to ${exhibitor.email}?`)) return;
    setBusy("send");
    try {
      await api.post(`/admin/exhibitors/${exhibitor.id}/send-invoice-email`);
      toast.success("Invoice email sent.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email.");
    } finally { setBusy(null); }
  };

  const markPaid = async () => {
    if (!window.confirm(`Mark payment received for ${exhibitor.company_name}? This will permanently assign their station, send the confirmation email, and advance production.`)) return;
    setBusy("pay");
    try {
      await api.post(`/admin/exhibitors/${exhibitor.id}/mark-paid`);
      toast.success("Payment marked received. Downstream flow triggered.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark paid.");
    } finally { setBusy(null); }
  };

  const cancel = async () => {
    if (!window.confirm(`Cancel this registration and release the station? This cannot be undone from the UI.`)) return;
    setBusy("cancel");
    try {
      await api.post(`/admin/exhibitors/${exhibitor.id}/cancel-registration`);
      toast.success("Registration cancelled and station released.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel registration.");
    } finally { setBusy(null); }
  };

  return (
    <div className="grid gap-6" data-testid="invoicing-panel">
      <div className="grid md:grid-cols-2 gap-6 text-sm">
        <Meta label="Payment Status" value={formatPaymentStatus(status)} />
        <Meta label="Invoice Amount" value={exhibitor.invoice_amount ? `$${Number(exhibitor.invoice_amount).toLocaleString(undefined,{minimumFractionDigits:2})}` : "—"} />
        <Meta label="Invoice Sent" value={exhibitor.invoice_sent_at ? new Date(exhibitor.invoice_sent_at).toLocaleString() : "—"} />
        <Meta label="Payment Received" value={exhibitor.payment_received_at ? new Date(exhibitor.payment_received_at).toLocaleString() : "—"} />
      </div>

      {!isPaid && !isCancelled && (
        <>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Invoice Number">
              <input value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })}
                placeholder="DA-1042" data-testid="inv-number"
                className="w-full h-11 px-3 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm" />
            </Field>
            <Field label="Invoice Date">
              <input type="date" value={f.invoice_date || ""} onChange={(e) => setF({ ...f, invoice_date: e.target.value })}
                data-testid="inv-date"
                className="w-full h-11 px-3 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm" />
            </Field>
            <Field label="Amount Invoiced ($)">
              <input type="number" step="0.01" min="0" value={f.invoice_amount} onChange={(e) => setF({ ...f, invoice_amount: e.target.value })}
                data-testid="inv-amount"
                className="w-full h-11 px-3 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm" />
            </Field>
            <Field label="Payment Due Date">
              <input type="date" value={f.payment_due_date || ""} onChange={(e) => setF({ ...f, payment_due_date: e.target.value })}
                data-testid="inv-due-date"
                className="w-full h-11 px-3 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm" />
            </Field>
            <Field label="Payment Received Date">
              <input type="date" value={f.payment_received_date || ""} onChange={(e) => setF({ ...f, payment_received_date: e.target.value })}
                data-testid="inv-received-date"
                className="w-full h-11 px-3 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm" />
            </Field>
            <Field label="Amount Paid ($)">
              <input type="number" step="0.01" min="0" value={f.amount_paid} onChange={(e) => setF({ ...f, amount_paid: e.target.value })}
                data-testid="inv-amount-paid"
                className="w-full h-11 px-3 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm" />
            </Field>
            <Field label="Invoice Status">
              <select value={f.invoice_status || "not_sent"} onChange={(e) => setF({ ...f, invoice_status: e.target.value })}
                data-testid="inv-status"
                className="w-full h-11 px-3 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm bg-white">
                <option value="not_sent">Not Sent</option>
                <option value="sent">Sent</option>
                <option value="past_due">Past Due</option>
                <option value="voided">Voided</option>
              </select>
            </Field>
            <Field label="Payment Status">
              <select value={status || "awaiting_invoice"} onChange={(e) => setPaymentStatus(e.target.value)}
                data-testid="pay-status"
                className="w-full h-11 px-3 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm bg-white">
                <option value="awaiting_invoice">Awaiting Invoice</option>
                <option value="invoice_sent">Invoice Sent</option>
                <option value="pending">Pending</option>
                <option value="partially_paid">Partially Paid</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
                <option value="waived">Waived</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </Field>
            <Field label="Invoice URL (optional pay link)">
              <input type="url" value={f.invoice_url} onChange={(e) => setF({ ...f, invoice_url: e.target.value })}
                placeholder="https://qbo.intuit.com/app/invoice/…" data-testid="inv-url"
                className="w-full h-11 px-3 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm" />
            </Field>
            <Field label="Accounting Notes (internal)">
              <textarea rows={3} value={f.accounting_notes} onChange={(e) => setF({ ...f, accounting_notes: e.target.value })}
                data-testid="inv-notes"
                placeholder="Internal notes — reconciliation, follow-ups, waivers…"
                className="w-full px-3 py-2 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm resize-y" />
            </Field>
          </div>
          <div className="flex flex-wrap gap-2 border-t border-zinc-100 pt-5">
            <button onClick={save} disabled={busy !== null}
              className="btn-outline-black h-10 px-4 text-xs disabled:opacity-40" data-testid="inv-save">
              {busy === "save" ? "Saving…" : "Save Invoice Details"}
            </button>
            <button onClick={sendEmail} disabled={busy !== null || !exhibitor.invoice_url}
              className="btn-outline-black h-10 px-4 text-xs disabled:opacity-40" data-testid="inv-send">
              {busy === "send" ? "Sending…" : "Send Invoice Email"}
            </button>
            <button onClick={markPaid} disabled={busy !== null}
              className="btn-black h-10 px-4 text-xs disabled:opacity-40" data-testid="inv-mark-paid">
              {busy === "pay" ? "Marking…" : "Mark Payment Received"}
            </button>
            <div className="flex-1" />
            <button onClick={cancel} disabled={busy !== null}
              className="h-10 px-4 text-xs uppercase tracking-[0.18em] text-red-700 border border-red-200 hover:bg-red-50 disabled:opacity-40" data-testid="inv-cancel">
              {busy === "cancel" ? "Cancelling…" : "Cancel Registration"}
            </button>
          </div>
        </>
      )}
      {isPaid && (
        <div className="border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="inv-paid-state">
          Payment received. Station permanently assigned; confirmation email has been sent to {exhibitor.email}.
        </div>
      )}
      {isCancelled && (
        <div className="border border-red-200 bg-red-50 p-4 text-sm text-red-900" data-testid="inv-cancelled-state">
          Registration cancelled. Station has been released. To re-enable this supplier, contact the platform admin.
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      {children}
    </div>
  );
}


function NotesSection({ notes, exhibitorId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [body, setBody] = useState("");
  const add = async () => {
    if (body.trim().length < 2) return toast.error("Note is too short.");
    setBusy(true);
    try {
      await api.post(`/admin/supplier-projects/${exhibitorId}/notes`, { body });
      setBody("");
      toast.success("Note added.");
      onChanged();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Could not add note.");
    } finally { setBusy(false); }
  };
  const remove = async (nid) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/admin/supplier-projects/${exhibitorId}/notes/${nid}`);
      onChanged();
    } catch { toast.error("Delete failed."); }
  };
  return (
    <Section testId="sp-notes" title="Internal Notes" icon={MessageSquare}>
      <div className="mb-4 grid gap-2">
        <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Internal-only note. Suppliers cannot see this."
          data-testid="sp-notes-input"
          className="rounded-none" />
        <div className="flex justify-end">
          <button onClick={add} disabled={busy || body.trim().length < 2}
                  className="btn-black h-9 px-4 text-xs disabled:opacity-40" data-testid="sp-notes-add">
            <Plus size={12} /> {busy ? "…" : "Add Note"}
          </button>
        </div>
      </div>
      {notes.length === 0 ? (
        <div className="text-sm text-zinc-500">No notes yet.</div>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {notes.map((n) => (
            <li key={n.id} className="py-3 flex items-start gap-3" data-testid={`sp-note-${n.id}`}>
              <div className="flex-1">
                <div className="text-sm whitespace-pre-wrap">{n.body}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
                  {n.author_email} · {dt(n.created_at)}
                </div>
              </div>
              <button onClick={() => remove(n.id)} className="text-zinc-400 hover:text-red-600" aria-label="Delete"
                      data-testid={`sp-note-remove-${n.id}`}>
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Section({ title, icon: Icon, children, testId, actionHref, actionLabel }) {
  return (
    <div className="bg-white border border-zinc-200 p-6 md:p-8" data-testid={testId}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Icon size={16} className="text-[#A39171]" strokeWidth={1.4} />
          <div className="serif text-2xl">{title}</div>
        </div>
        {actionHref && (
          <Link to={actionHref} className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 hover:text-black inline-flex items-center gap-2">
            {actionLabel} <ExternalLink size={11} />
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

function HKpi({ label, value, tone }) {
  return (
    <div className="bg-black px-5 py-3">
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">{label}</div>
      <div className={`serif text-lg mt-1 tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}
function OverviewStat({ label, value, icon: Icon, tone }) {
  return (
    <div className="border border-zinc-200 p-5">
      <div className="flex items-center gap-2"><Icon size={14} className={tone} strokeWidth={1.4} /><span className="overline">{label}</span></div>
      <div className={`serif text-3xl mt-3 tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}
function MiniKpi({ label, value, tone }) {
  return (
    <div className="border border-zinc-200 p-4">
      <div className="overline">{label}</div>
      <div className={`serif text-2xl mt-2 tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}
function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{label}</div>
      <div className="text-sm mt-1">{value || <span className="text-zinc-400">—</span>}</div>
    </div>
  );
}

function WebsiteField({ exhibitor }) {
  const url = exhibitor.website;
  if (!url) {
    return (
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Website</div>
        <div className="text-sm mt-1 text-zinc-400">—</div>
      </div>
    );
  }
  const href = /^https?:\/\//.test(url) ? url : `https://${url}`;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Website</div>
      <a href={href} target="_blank" rel="noreferrer"
         className="text-sm mt-1 inline-flex items-center gap-1 text-[#A39171] hover:underline underline-offset-4 break-all"
         data-testid="sp-website-link">
        {url} <ExternalLink size={11} className="shrink-0" />
      </a>
    </div>
  );
}

/**
 * Editable Supplier Profile fields — Description, Contact Title, Website,
 * Products & Services (freeform), Business Areas (multi-select),
 * Matchmaking Preferences (multi-select). Used by admin to correct data
 * captured during Prime Supplier onboarding.
 */
const PROFILE_VOCAB = [
  "Construction", "Engineering", "Architecture", "Electrical", "Mechanical",
  "HVAC", "Security", "Risk Management", "IT", "Cybersecurity",
  "Staffing", "Marketing", "Furniture", "Printing", "Promotional Products",
  "Facilities Management", "Janitorial", "Landscaping", "Food Services",
  "Transportation", "Logistics", "Professional Services", "Consulting",
  "Financial Services", "Legal", "Other",
];

function SupplierProfileEditor({ exhibitor, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    contact_title: exhibitor.contact_title || "",
    website: exhibitor.website || "",
    description: exhibitor.description || "",
    products_services: exhibitor.products_services || "",
    business_areas: exhibitor.business_areas || [],
    matchmaking_preferences: exhibitor.matchmaking_preferences || [],
  });
  const [saving, setSaving] = useState(false);
  const descCount = (form.description || "").length;

  const toggle = (key, v) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v],
    }));

  const save = async () => {
    if (descCount > 500) return toast.error("Description must be 500 characters or fewer.");
    setSaving(true);
    try {
      await api.put(`/admin/exhibitors/${exhibitor.id}`, {
        contact_title: form.contact_title.trim(),
        website: form.website.trim(),
        description: form.description.trim(),
        products_services: form.products_services.trim(),
        business_areas: form.business_areas,
        matchmaking_preferences: form.matchmaking_preferences,
      });
      toast.success("Supplier profile updated.");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-8 border-t border-zinc-100 pt-6" data-testid="sp-profile-editor">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline">Supplier Profile</div>
          <div className="text-xs text-zinc-500 mt-1">
            These fields populate the public Supplier Profile at /supplier/{exhibitor.id}.
          </div>
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-[11px] uppercase tracking-[0.18em] px-3 py-1 border border-zinc-300 hover:border-black"
          data-testid="sp-profile-toggle">
          {open ? "Cancel" : "Edit"}
        </button>
      </div>

      {!open ? (
        <div className="grid md:grid-cols-2 gap-6 text-sm">
          <Meta label="Description" value={exhibitor.description || "—"} />
          <Meta label="Products & Services" value={exhibitor.products_services || "—"} />
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Product & Service Categories</div>
            <div className="text-sm mt-1">
              {(exhibitor.business_areas || []).length === 0 ? (
                <span className="text-zinc-400">—</span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {exhibitor.business_areas.map((a) => (
                    <span key={a} className="text-[11px] uppercase tracking-[0.16em] border border-black px-2 py-0.5">{a}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Business Matchmaking Interests</div>
            <div className="text-sm mt-1">
              {(exhibitor.matchmaking_preferences || []).length === 0 ? (
                <span className="text-zinc-400">—</span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {exhibitor.matchmaking_preferences.map((a) => (
                    <span key={a} className="text-[11px] uppercase tracking-[0.16em] border border-[#A39171] text-[#7A6A4A] px-2 py-0.5">{a}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid gap-5">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="grid gap-1">
              <span className="overline">Contact Title</span>
              <input value={form.contact_title} onChange={(e) => setForm({ ...form, contact_title: e.target.value })}
                data-testid="sp-edit-contact-title"
                className="h-11 px-3 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm" />
            </label>
            <label className="grid gap-1">
              <span className="overline">Company Website</span>
              <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                data-testid="sp-edit-website"
                placeholder="https://example.com"
                className="h-11 px-3 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm" />
            </label>
          </div>
          <label className="grid gap-1">
            <span className="overline flex items-center justify-between">
              <span>Company Description</span>
              <span className="text-[10px] normal-case tracking-normal text-zinc-500">{descCount}/500</span>
            </span>
            <Textarea rows={4} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value.slice(0, 500) })}
              data-testid="sp-edit-description"
              className="rounded-none" />
          </label>
          <label className="grid gap-1">
            <span className="overline">Products & Services</span>
            <Textarea rows={3} value={form.products_services}
              onChange={(e) => setForm({ ...form, products_services: e.target.value })}
              data-testid="sp-edit-products-services"
              className="rounded-none" />
          </label>
          <div>
            <div className="overline mb-2">Product & Service Categories</div>
            <div className="flex flex-wrap gap-1.5" data-testid="sp-edit-business-areas">
              {PROFILE_VOCAB.map((v) => {
                const on = form.business_areas.includes(v);
                return (
                  <button key={v} type="button" onClick={() => toggle("business_areas", v)}
                    className={`px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] border ${on ? "bg-black text-white border-black" : "bg-white border-zinc-300 hover:border-black"}`}>
                    {v}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="overline mb-2">Business Matchmaking Interests</div>
            <div className="flex flex-wrap gap-1.5" data-testid="sp-edit-matchmaking">
              {PROFILE_VOCAB.map((v) => {
                const on = form.matchmaking_preferences.includes(v);
                return (
                  <button key={v} type="button" onClick={() => toggle("matchmaking_preferences", v)}
                    className={`px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] border ${on ? "bg-[#A39171] text-white border-[#A39171]" : "bg-white border-zinc-300 hover:border-[#A39171]"}`}>
                    {v}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-zinc-100 pt-4">
            <button onClick={() => setOpen(false)} disabled={saving}
              className="h-10 px-4 text-[11px] uppercase tracking-[0.18em] border border-zinc-300">
              Cancel
            </button>
            <button onClick={save} disabled={saving}
              className="btn-black h-10 px-5 text-[11px] disabled:opacity-40"
              data-testid="sp-edit-save">
              {saving ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function IndustryCategoryField({ exhibitor, onSaved }) {
  const [categories, setCategories] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    api.get("/industry-categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);
  const save = async (val) => {
    setSaving(true);
    try {
      await api.put(`/admin/exhibitors/${exhibitor.id}`, { industry_category: val });
      toast.success("Industry category updated");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setSaving(false); }
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Industry Category</div>
      <select value={exhibitor.industry_category || ""}
              onChange={(e) => save(e.target.value)}
              disabled={saving}
              data-testid="sp-industry-category"
              className="mt-1 text-sm border border-zinc-200 hover:border-black px-2 py-1.5 bg-white outline-none w-full max-w-xs">
        <option value="">— Not set —</option>
        {categories.map((c) => (
          <option key={c.key} value={c.key}>{c.label} · S-{String(c.range.start).padStart(2, "0")}–S-{String(c.range.end).padStart(2, "0")}</option>
        ))}
      </select>
    </div>
  );
}
